import { useRouter } from 'found';
import includes from 'lodash/includes';
import PropTypes from 'prop-types';
import Menu from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import ShareIcon from '@mui/icons-material/Reply';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { FacebookShareButton, TwitterShareButton } from 'react-share';
import FlatButton from 'components/FlatButton';
import FacebookIcon from 'components/icons/Facebook';
import TwitterIcon from 'components/icons/Twitter';
import { useTheme } from '@mui/material/styles';

const ShareButton = ({
  hashtags,
  onClick,
  onShareButtonClick,
  onShareWindowClose,
  title,
  url,
  icon,
  setSharing,
  ...props
}) => {
  const {
    match: {
      location: { pathname },
    },
  } = useRouter();
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const hashtag = useMemo(
    () => (Array.isArray(hashtags) ? hashtags[0] : hashtags),
    [hashtags],
  );
  const shareUrl = useMemo(() => url || `${process.env.APP_URL}${pathname}`, [
    pathname,
    url,
  ]);
  const hasSponsoredHashtag = useMemo(
    () => hashtags && hashtags.length && includes(hashtags, 'sponsored'),
    [hashtags],
  );
  const onButtonClick = useCallback(
    e => {
      if (onClick) {
        onClick(e);
      }
      setAnchor(e.currentTarget);
      if (setSharing) {
        setSharing(true);
      }
      setOpen(true);
    },
    [onClick, setAnchor, setOpen, setSharing],
  );
  const onRequestClose = useCallback(() => {
    setOpen(false);
    if (setSharing) {
      setSharing(false);
    }
  }, [setOpen, setSharing]);
  const onItemClick = useCallback(
    provider => {
      if (onShareButtonClick) {
        onShareButtonClick(provider);
      }
      onRequestClose();
    },
    [onRequestClose, onShareButtonClick],
  );

  const twitterHasTag = useMemo(
    () => (hashtags ? hashtags.concat('sponsored') : ['sponsored']),
    [hashtags],
  );
  return (
    <Fragment>
      <FlatButton
        {...props}
        forceLabel
        icon={icon || <ShareIcon htmlColor='#fff' />}
        onClick={onButtonClick}
      />
      <Popover
        anchorEl={anchor}
        anchorOrigin={useMemo(
          () => ({
            vertical: 'top',
            horizontal: 'left',
          }),
          [],
        )}
        transformOrigin={useMemo(
          () => ({
            vertical: 'top',
            horizontal: 'left',
          }),
          [],
        )}
        onClose={onRequestClose}
        open={open}
      >
        <Menu
          listStyle={useMemo(
            () => ({
              paddingTop: 0,
              paddingBottom: 0,
              display: 'grid',
            }),
            [],
          )}
        >
          <MenuItem style={{ textAlign: 'left' }} onClick={onRequestClose}>
            <FacebookShareButton
              // facebook requires the actual # symbol, and only one hashtag
              hashtag={useMemo(
                () => hashtag
                  && (hashtag.charAt(0) !== '#' ? `#${hashtag}` : hashtag),
                [hashtag],
              )}
              beforeOnClick={useCallback(() => onItemClick('facebook'), [
                onItemClick,
              ])}
              onShareWindowClose={onShareWindowClose}
              // since we can only add one hashtag, we add #sponsored here
              quote={useMemo(
                () => (hasSponsoredHashtag ? title : `${title} #sponsored`),
                [hasSponsoredHashtag, title],
              )}
              url={shareUrl}
            >
              <Typography>
                <ListItemIcon>
                  <FacebookIcon color={theme.palette.text.primary} />
                </ListItemIcon>
                Facebook
              </Typography>
            </FacebookShareButton>
          </MenuItem>
          <MenuItem style={{ textAlign: 'left' }} onClick={onRequestClose}>
            <TwitterShareButton
              // twitter allows multiple, but should not have the # symbol
              hashtags={twitterHasTag}
              beforeOnClick={useCallback(() => onItemClick('twitter'), [
                onItemClick,
              ])}
              onShareWindowClose={onShareWindowClose}
              title={title}
              url={shareUrl}
              via='ripplestreetfun'
            >
              <Typography>
                <ListItemIcon>
                  <TwitterIcon color={theme.palette.text.primary} />
                </ListItemIcon>
                X
              </Typography>
            </TwitterShareButton>
          </MenuItem>
          {navigator?.share && (
            <MenuItem
              onClick={() => {
                navigator.share({
                  title,
                  text: hashtags && hashtags.concat('sponsored'),
                  url: shareUrl,
                });
              }}
            >
              <ListItemIcon>
                <ShareOutlined htmlColor={theme.palette.text.primary} />
              </ListItemIcon>
              <Typography>Others</Typography>
            </MenuItem>
          )}
        </Menu>
      </Popover>
    </Fragment>
  );
};

ShareButton.propTypes = {
  hashtags: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  icon: PropTypes.node,
  onClick: PropTypes.func,
  onShareButtonClick: PropTypes.func,
  onShareWindowClose: PropTypes.func,
  title: PropTypes.string,
  url: PropTypes.string,
  setSharing: PropTypes.func,
};

export default ShareButton;
